import type { OtlpAttributes, OtlpExporter } from './otlp-exporter';
import { createSpanId, createTraceId, normalizeOtlpId, OtlpSeverity, OtlpSpanKind } from './otlp-exporter';

type AppInsightsBaseDataLike = {
	id?: string;
	name?: string;
	duration?: number | string;
	startTime?: Date | string;
	success?: boolean;
	responseCode?: number | string;
	target?: string;
	data?: string;
	type?: string;
	message?: string;
	properties?: Record<string, unknown>;
	measurements?: Record<string, number>;
	metrics?: { name: string; value: number }[];
	exceptions?: { message?: string }[];
	exception?: { message?: string };
};

export type AppInsightsTelemetryItemLike = {
	name?: string;
	time?: string;
	baseType?: string;
	baseData?: AppInsightsBaseDataLike;
	data?: Record<string, unknown>;
	tags?: Record<string, string>;
	ext?: { trace?: { traceID?: string; parentID?: string } };
};

const typeNames: Record<string, string> = {
	PageviewData: 'pageView',
	EventData: 'event',
	RemoteDependencyData: 'dependency',
	ExceptionData: 'exception',
	MessageData: 'trace',
	MetricData: 'metric',
	PageviewPerformanceData: 'pageViewPerformance'
};

function parseDuration(value: unknown): number | null {
	if (typeof value === 'number')
		return isFinite(value) ? value : null;
	if (typeof value !== 'string' || !value)
		return null;
	if (/^\d+(\.\d+)?$/.test(value))
		return Number(value);
	const match = /^(?:(\d+)\.)?(\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)$/.exec(value);
	if (!match)
		return null;
	const [, days, hours, minutes, seconds] = match;
	return ((Number(days ?? 0) * 24 + Number(hours)) * 60 + Number(minutes)) * 60000 + Number(seconds) * 1000;
}

function flattenProperties(source: Record<string, unknown> | undefined, target: OtlpAttributes) {
	if (!source)
		return;
	for (const [key, value] of Object.entries(source)) {
		if (value === null || value === undefined)
			continue;
		target[key] = typeof value === 'object' ? JSON.stringify(value) : value as string | number | boolean;
	}
}

/**
 * Extracts trace and span ids from an Application Insights dependency id, which is either a bare W3C span id or
 * the request-id form `|<traceId>.<spanId>.`.
 */
export function parseDependencyId(id: string | undefined): { traceId?: string; spanId?: string } {
	const match = /^\|?([0-9a-f]{32})\.([0-9a-f]{16})\.?$/i.exec(id ?? '');
	if (match)
		return { traceId: match[1].toLowerCase(), spanId: match[2].toLowerCase() };
	return { spanId: id && /^[0-9a-f]{16}$/i.test(id) ? id.toLowerCase() : undefined };
}

/**
 * Collapses ids, tokens, and query strings out of a dependency name so it can be used as a low-cardinality metric dimension.
 */
export function normalizeDependencyName(name: string): string {
	return (name || '')
		.replace(/\?.*$/, '')
		.replace(/https?:\/\/[^/]+/i, '')
		.replace(/\/[^/]*\d[^/]*(?=\/|$)/g, '/{id}');
}

function forwardItem(exporter: OtlpExporter, item: AppInsightsTelemetryItemLike) {
	const baseData: AppInsightsBaseDataLike = item.baseData ?? {};
	const type = (item.baseType && typeNames[item.baseType]) || item.baseType || 'unknown';
	if (type === 'dependency' && [baseData.data, baseData.target, baseData.name].some(value => typeof value === 'string' && value.startsWith(exporter.baseUrl)))
		return;
	const endTimeMs = item.time ? Date.parse(item.time) : Date.now();
	const traceId = normalizeOtlpId(item.ext?.trace?.traceID ?? item.tags?.['ai.operation.id'], 32) ?? createTraceId();
	const parentSpanId = normalizeOtlpId(item.ext?.trace?.parentID ?? item.tags?.['ai.operation.parentId'], 16);

	const attributes: OtlpAttributes = { 'ai.type': type };
	flattenProperties(baseData.properties, attributes);
	flattenProperties(item.data, attributes);
	flattenProperties(baseData.measurements, attributes);

	const name = baseData.name ?? item.name ?? type;
	const duration = parseDuration(baseData.duration) ?? parseDuration(baseData.properties?.duration);

	if (duration !== null && (type === 'dependency' || type === 'pageView' || type === 'event')) {
		const isDependency = type === 'dependency';
		if (isDependency) {
			attributes['url.full'] = baseData.target && baseData.data ? baseData.data : baseData.target;
			attributes['http.response.status_code'] = baseData.responseCode;
			attributes['dependency.type'] = baseData.type;
		}
		const startTimeMs = baseData.startTime ? new Date(baseData.startTime).getTime() : endTimeMs - duration;
		const dependencyIds = isDependency ? parseDependencyId(baseData.id) : {};
		exporter.exportSpan({
			name: type === 'pageView' ? `pageView ${name}` : name,
			traceId: dependencyIds.traceId ?? traceId,
			spanId: dependencyIds.spanId ?? createSpanId(),
			parentSpanId,
			startTimeMs,
			endTimeMs: startTimeMs + duration,
			kind: isDependency ? OtlpSpanKind.Client : OtlpSpanKind.Internal,
			isError: isDependency && baseData.success === false,
			attributes
		});

		const histogramAttributes: OtlpAttributes = isDependency
			? { name: normalizeDependencyName(name), status: String(baseData.responseCode ?? '') }
			: { name, origin: attributes.origin as string };
		exporter.recordHistogram(isDependency ? 'browser.dependency.duration' : `browser.${type === 'pageView' ? 'page_view' : 'event'}.duration`, duration, histogramAttributes);

		if (isDependency)
			return;
	}

	if (type === 'dependency')
		return;

	if (type === 'metric' && Array.isArray(baseData.metrics)) {
		for (const metric of baseData.metrics)
			exporter.recordHistogram(`browser.metric.${metric.name}`, metric.value, {}, '');
	}

	const message = type === 'exception'
		? baseData.exceptions?.[0]?.message ?? baseData.exception?.message ?? 'exception'
		: type === 'trace' ? baseData.message : `${type}: ${name}`;
	exporter.exportLog({
		timeMs: endTimeMs,
		severity: type === 'exception' ? OtlpSeverity.Error : baseData.properties?.isWarning === 'true' ? OtlpSeverity.Warning : OtlpSeverity.Information,
		body: message ?? '',
		traceId,
		spanId: parentSpanId,
		attributes: { ...attributes, 'event.name': name }
	});
}

const dependencyTrackingOwnerKey = '__cognitoLocalOtlpDependencyTracking';

/**
 * Claims automatic fetch/XHR tracking for one Application Insights instance per page, so apps that share a page
 * (e.g. the form runtime embedded in the SPA) do not each record the same requests. Returns whether the caller owns it.
 */
export function claimLocalDependencyTracking(serviceName: string): boolean {
	const host = globalThis as Record<string, unknown>;
	host[dependencyTrackingOwnerKey] = host[dependencyTrackingOwnerKey] ?? serviceName;
	return host[dependencyTrackingOwnerKey] === serviceName;
}

/**
 * Builds an Application Insights telemetry initializer that forwards every item to an OTLP exporter and then drops it,
 * so local development can observe the same client telemetry that production sends to Application Insights.
 * Items with a duration become spans (and duration histograms); events, page views, traces, and exceptions are also
 * emitted as structured logs so their full property bag is queryable.
 */
export function createAppInsightsOtlpForwarder(exporter: OtlpExporter): (item: AppInsightsTelemetryItemLike) => boolean {
	return function forwardToOtlp(item) {
		try {
			forwardItem(exporter, item);
		}
		catch (e) {
			console.warn('Failed to forward telemetry to OTLP', e);
		}
		return false;
	};
}
